import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { apiDelete, apiGet, apiPost } from "../../lib/api.js";
import { MutationError, Section, formatDate } from "../shared.js";

type InvoiceShare = {
  id: string;
  createdAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  lastAccessAt?: string | null;
  accessCount: number;
};

type CreatedInvoiceShare = InvoiceShare & { url: string };

export function InvoiceSharing({
  invoiceId,
  enabled,
}: {
  invoiceId: string;
  enabled: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [created, setCreated] = useState<CreatedInvoiceShare | null>(null);
  const [copied, setCopied] = useState(false);
  const shares = useQuery({
    queryKey: ["invoice", invoiceId, "shares"],
    queryFn: () =>
      apiGet<{ data: InvoiceShare[] }>(`/invoices/${invoiceId}/shares`).then(
        (response) => response.data,
      ),
    enabled,
  });
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ data: CreatedInvoiceShare }>(`/invoices/${invoiceId}/shares`),
    onSuccess: async ({ data }) => {
      setCreated(data);
      setCopied(false);
      await queryClient.invalidateQueries({
        queryKey: ["invoice", invoiceId, "shares"],
      });
    },
  });
  const revoke = useMutation({
    mutationFn: (shareId: string) =>
      apiDelete(`/invoices/${invoiceId}/shares/${shareId}`),
    onSuccess: async (_, shareId) => {
      if (created?.id === shareId) setCreated(null);
      await queryClient.invalidateQueries({
        queryKey: ["invoice", invoiceId, "shares"],
      });
    },
  });
  const copy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.url);
    setCopied(true);
  };

  if (!enabled) return null;
  return (
    <Section
      title={t("invoices.sharing.title", {
        defaultValue: "Secure invoice links",
      })}
    >
      <p className="mb-4 text-sm text-app-muted">
        {t("invoices.sharing.hint", {
          defaultValue:
            "Create a private download link that expires after three months. The link is shown only once.",
        })}
      </p>
      <Button
        onClick={() => create.mutate()}
        loading={create.isPending || undefined}
      >
        {t("invoices.sharing.create", { defaultValue: "Create share link" })}
      </Button>
      {created ? (
        <div className="mt-4 rounded-lg border border-success-border bg-success-soft p-3">
          <label className="block text-sm font-medium text-success">
            {t("invoices.sharing.newLink", { defaultValue: "New share link" })}
            <input
              readOnly
              value={created.url}
              className="mt-2 block w-full rounded-lg border border-success-border bg-app-surface px-3 py-2 font-mono text-xs text-app-text"
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
          <Button
            className="mt-2"
            size="small"
            variant="outlined"
            severity="secondary"
            onClick={copy}
          >
            {copied
              ? t("invoices.sharing.copied", { defaultValue: "Copied" })
              : t("invoices.sharing.copy", { defaultValue: "Copy link" })}
          </Button>
        </div>
      ) : null}
      <MutationError error={create.error || revoke.error} />
      {shares.data?.length ? (
        <ul className="mt-5 divide-y divide-app-border rounded-lg border border-app-border">
          {shares.data.map((share) => {
            const inactive =
              Boolean(share.revokedAt) ||
              new Date(share.expiresAt) <= new Date();
            return (
              <li
                key={share.id}
                className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 text-sm"
              >
                <div>
                  <span className="font-medium text-app-text">
                    {inactive
                      ? t("invoices.sharing.inactive", {
                          defaultValue: "Inactive link",
                        })
                      : t("invoices.sharing.active", {
                          defaultValue: "Active link",
                        })}
                  </span>
                  <span className="block text-app-muted">
                    {t("invoices.sharing.expires", {
                      defaultValue: "Expires {{date}} · {{count}} downloads",
                      date: formatDate(share.expiresAt),
                      count: share.accessCount,
                    })}
                  </span>
                </div>
                {!inactive ? (
                  <Button
                    size="small"
                    severity="danger"
                    loading={
                      (revoke.isPending && revoke.variables === share.id) ||
                      undefined
                    }
                    onClick={() => revoke.mutate(share.id)}
                  >
                    {t("invoices.sharing.revoke", { defaultValue: "Revoke" })}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Section>
  );
}
